import Icon from '@mui/icons-material/Error'
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import usePutConsentByID from '../../api/v1/accounts/usePutConsentByID'
import CustomDialog from '../../components/CustomDialog'
import BodyPaper from '../../components/Layout/BodyPaper'
import Header from '../../components/Layout/Header'
import StyledTextField from '../../components/Layout/StyledTextField'
import { Language, getLanguageOptions, stringToLanguage } from '../../language'
import { AlertData, AlertType, useAlert } from '../../providers/alert'
import { AuthState, useAuth } from '../../providers/auth'
import { useConfig } from '../../providers/envConfig'
import { FirstPartyPlatformType } from '../../types/Account'

type TouchedFields = {
  firstName: boolean
  email: boolean
  confirmEmail: boolean
}

type ErrorFields = {
  firstName: string
  email: string
  confirmEmail: string
}

/**
 * assembleAlert is a utility function that creates an alert object with a
 * message and severity.
 * @param message - The message to be displayed in the alert.
 * @param severity - The severity level of the alert (e.g., error, info,
 *                   success, warning).
 */
const assembleAlert = (message: string, severity: AlertType): AlertData => {
  return {
    severity: severity,
    msg: message,
    duration: 5000,
  }
}

interface ChildLandingPageProps {
  /**
   * `handleCancel` fires when the user clicks the cancel button. If not
   * provided, it defaults to redirecting to the Bethesda.net homepage.
   */
  handleCancel?: () => void
}

const getPlatformTranslationKey = (platform: FirstPartyPlatformType | '') => {
  if (platform !== '') return `vpc:platform.${platform.toLowerCase()}`
  return 'vpc:requestConsentConfirmationDialog.unknownPlatform'
}

const getDisplayName = (authState: AuthState) => {
  if (
    authState?.accountInfo?.firstPartyPlatformType == FirstPartyPlatformType.XBOXLIVE &&
    authState?.userInfo.suffix
  ) {
    return `${authState?.userInfo.username}#${authState?.userInfo.suffix}`
  }
  return authState?.userInfo.username || ''
}

/**
 * `ChildLandingPage` is a React functional component that renders a form for collecting
 * a requestor's first name and their approver's email address. It validates the input fields
 * and sends a parental consent request upon submission.
 */
const ChildLandingPage: React.FC<ChildLandingPageProps> = ({ handleCancel }) => {
  const theme = useTheme()
  const envConfig = useConfig()
  const { t } = useTranslation()
  const { showAlert, clearAlert } = useAlert()
  const { authState, refreshAuthState, logout } = useAuth()
  const navigate = useNavigate()
  const { lang } = useParams<{ lang: string }>()

  const platform = authState?.accountInfo?.firstPartyPlatformType || ''
  const initialFirstName = authState?.accountInfo?.givenName || ''
  const initialEmail = authState?.accountInfo?.consent?.parentEmail || ''
  const parentLanguage = authState?.accountInfo?.consent?.parentLanguage
  const [firstName, setFirstName] = useState<string>(initialFirstName)
  const [email, setEmail] = useState<string>(initialEmail)
  const [confirmEmail, setConfirmEmail] = useState<string>(initialEmail)
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(() => {
    const { lang: parsedLang, valid } = parentLanguage
      ? stringToLanguage(parentLanguage)
      : stringToLanguage(lang)
    return valid ? parsedLang : Language.English
  })

  const [errors, setErrors] = useState<ErrorFields>({
    firstName: '',
    email: '',
    confirmEmail: '',
  })

  const {
    data: putResponse,
    loading,
    error,
    callFetch,
  } = usePutConsentByID(
    authState?.accountInfo?.id || '',
    firstName,
    email,
    authState?.jwtRaw || '',
    selectedLanguage,
  )

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState<boolean>(false)

  // prevent the user from clicking the send button to make requests multiple times
  const [disableConfirmationSendButton, setDisableConfirmationSendButton] = useState<boolean>(false)

  // Track if the fields have been touched, to avoid the errors populating on page load
  const [touched, setTouched] = useState<TouchedFields>({
    firstName: initialFirstName !== '',
    email: initialEmail !== '',
    confirmEmail: initialEmail !== '',
  })
  const emailRegex = /\S+@\S+\.\S+/

  // on successful request, refresh auth state to navigate to the next workflow page
  useEffect(() => {
    if (putResponse !== null && email !== null && !loading && !error) {
      clearAlert()
      showAlert(
        assembleAlert(
          t('vpc:requestConsentConfirmationDialog.sendSuccess', { email: email }),
          AlertType.SUCCESS,
        ),
      )
      setConfirmationDialogOpen(false)
      refreshAuthState()
    }
  }, [putResponse, email, loading, error, refreshAuthState])

  // on failed request, show an error alert and allow the user to retry after a pause
  useEffect(() => {
    if (error) {
      clearAlert()
      showAlert(assembleAlert(t('errors.failedToRequestParentalConsent'), AlertType.ERROR))

      // add a short pause before allowing retry
      setTimeout(() => {
        setDisableConfirmationSendButton(false)
      }, 1000)
    }
  }, [error, t])

  const submitParentalConsent = useCallback(async () => {
    const accountId = authState.accountInfo?.id
    const token = authState.jwtRaw

    if (!accountId || !token) {
      showAlert(assembleAlert(t('errors.missingAuth'), AlertType.ERROR))
      return
    }

    callFetch()
  }, [envConfig, authState, callFetch, t, showAlert])

  // Validates the form fields, setting any errors into the errors state.
  const validate = useCallback(() => {
    const newErrors = { firstName: '', email: '', confirmEmail: '' }

    if (touched.firstName) {
      if (!firstName) {
        newErrors.firstName = 'vpc:childLandingPage:errors.firstNameRequired'
      } else if (firstName.length > 100) {
        newErrors.firstName = 'vpc:childLandingPage:errors.firstNameLength'
      }
    }

    if (touched.email) {
      if (!email) {
        newErrors.email = 'vpc:childLandingPage:errors.emailRequired'
      } else if (!emailRegex.test(email)) {
        newErrors.email = 'vpc:childLandingPage:errors.emailInvalid'
      } else if (email.length > 100) {
        newErrors.email = 'vpc:childLandingPage:errors.emailLength'
      }
    }

    if (touched.confirmEmail) {
      if (!confirmEmail) {
        newErrors.confirmEmail = 'vpc:childLandingPage:errors.confirmEmailRequired'
      } else if (email.toLowerCase() !== confirmEmail.toLowerCase()) {
        newErrors.confirmEmail = 'vpc:childLandingPage:errors.emailsDoNotMatch'
      } else if (!emailRegex.test(confirmEmail)) {
        newErrors.confirmEmail = 'vpc:childLandingPage:errors.emailInvalid'
      } else if (confirmEmail.length > 100) {
        newErrors.confirmEmail = 'vpc:childLandingPage:errors.emailLength'
      }
    }

    setErrors(newErrors)
  }, [firstName, email, confirmEmail, touched])

  // Check if there are any errors and if all fields have been touched
  const isValid = useCallback(() => {
    return (
      Object.values(errors).every((error) => error === '') &&
      Object.values(touched).every((touched) => touched)
    )
  }, [errors, touched])

  useEffect(() => {
    if (touched.firstName || touched.email || touched.confirmEmail) {
      validate()
    }
  }, [validate, touched])

  const handleFirstNameChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setTouched((prevTouched) => ({
        ...prevTouched,
        firstName: true,
      }))

      const value = e.target.value
      setFirstName(value)
    },
    [],
  )

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setTouched((prevTouched) => ({
        ...prevTouched,
        email: true,
      }))

      const value = e.target.value
      setEmail(value)
    },
    [],
  )

  const handleConfirmEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setTouched((prevTouched) => ({
        ...prevTouched,
        confirmEmail: true,
      }))

      const value = e.target.value
      setConfirmEmail(value)
    },
    [],
  )

  const handleLanguageChange = useCallback((e: SelectChangeEvent<Language>) => {
    setSelectedLanguage(e.target.value as Language)
  }, [])

  /**
   * handleSubmit sets all input fields to 'touched', checks if their values
   * are valid, and if so, opens the confirmation dialog. If the user is not
   * logged in, or if the values are invalid, it shows an error alert.
   */
  const handleSubmit = useCallback(async () => {
    setTouched({
      firstName: true,
      email: true,
      confirmEmail: true,
    })
    if (!isValid()) return
    if (!authState.accountInfo?.id || !authState.jwt) {
      showAlert(assembleAlert(t('errors.notLoggedIn'), AlertType.ERROR))
      return
    }
    setConfirmationDialogOpen(true)
  }, [submitParentalConsent, showAlert, isValid, authState])

  const defaultHandleCancel = useCallback(() => {
    // Go to the BNET homepage
    // TODO: Add a confirmation dialog to confirm the user wants to leave
    window.location.href = envConfig.bnetSiteUrl
  }, [envConfig])

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  /**
   * onClickConfirmationDialogSend is called when the user clicks the send
   * button in the confirmation dialog. It disables the button, shows an info
   * alert, and calls the submitParentalConsent function to request verifiable
   * parental consent.
   */
  const onClickConfirmationDialogSend = useCallback(async () => {
    setDisableConfirmationSendButton(true)
    showAlert(
      assembleAlert(
        t('vpc:requestConsentConfirmationDialog:sendingAlert', { email: email }),
        AlertType.INFO,
      ),
    )
    await submitParentalConsent()
  }, [showAlert, t, email, submitParentalConsent])

  return (
    <Box sx={{ minHeight: '70vh', display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
      <Header
        showPlatformDisplayName
        title={t('vpc:childLandingPage:title')}
        subtitle={t('vpc:childLandingPage:subtitle')}
      />
      <BodyPaper
        showExpiration={true}
        showLogoutButton={true}
        sx={{
          p: 5,
          pb: 0,

          [theme.breakpoints.down('lg')]: {
            p: 0,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            position: 'relative',

            [theme.breakpoints.down('lg')]: {
              mt: 3.75,
              mx: 2.5,
            },
          }}
        >
          <Box sx={{ mb: 3.75, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Typography variant="h6" component="h3" sx={{ mb: 1.5 }}>
              {t('vpc:childLandingPage.yourName')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2.5,
                flexWrap: 'wrap',
              }}
            >
              <StyledTextField
                data-testid="first-name-input"
                label={t('vpc:childLandingPage:firstNameLabel')}
                value={firstName}
                onChange={(e) => handleFirstNameChange(e)}
                onBlur={(e) => handleFirstNameChange(e)}
                error={!!errors.firstName}
                helperText={t(errors.firstName)}
              />
            </Box>
            <Divider sx={{ mb: 1.5, mt: 1.5 }} />
            <Typography variant="h6" component="h3" sx={{ mb: 1.5 }}>
              {t('vpc:childLandingPage:parentEmail')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2.5,
                flexWrap: 'wrap',
              }}
            >
              <StyledTextField
                data-testid="email-input"
                label={t('vpc:childLandingPage:emailLabel')}
                value={email}
                onChange={(e) => handleEmailChange(e)}
                onBlur={(e) => handleEmailChange(e)}
                error={!!errors.email}
                helperText={t(errors.email)}
              />
              <StyledTextField
                data-testid="confirm-email-input"
                label={t('vpc:childLandingPage:confirmEmailLabel')}
                value={confirmEmail}
                onChange={(e) => handleConfirmEmailChange(e)}
                onBlur={(e) => handleConfirmEmailChange(e)}
                error={!!errors.confirmEmail}
                helperText={t(errors.confirmEmail)}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2.5,
                mt: 1.5,
              }}
            >
              <FormControl sx={{ width: theme.spacing(50) }}>
                <InputLabel id="language-select-label">
                  {t('vpc:childLandingPage:languageLabel')}
                </InputLabel>
                <Select
                  data-testid="language-select"
                  labelId="language-select-label"
                  value={selectedLanguage}
                  label={t('vpc:childLandingPage:languageLabel')}
                  onChange={handleLanguageChange}
                >
                  {getLanguageOptions().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2.5,
              mt: 8,
              mb: 10,
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              data-testid="child-landing-send-button"
              variant="contained"
              onClick={handleSubmit}
            >
              <Typography variant="buttonMedium" component="h3">
                {t('vpc:childLandingPage:sendButton')}
              </Typography>
            </Button>
            <Button
              data-testid="child-landing-cancel-button"
              variant="outlined"
              onClick={handleCancel || defaultHandleCancel}
            >
              <Typography variant="buttonMedium" component="h3">
                {t('vpc:childLandingPage:cancelButton')}
              </Typography>
            </Button>
          </Box>
        </Box>
      </BodyPaper>
      <CustomDialog
        isOpen={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        icon={
          <Icon htmlColor={theme.palette.warning.light} sx={{ width: '4rem', height: '4rem' }} />
        }
        title={t('vpc:requestConsentConfirmationDialog:title')}
        actions={[
          {
            label: t('vpc:requestConsentConfirmationDialog:cancelButton'),
            onClick: () => setConfirmationDialogOpen(false),
            testId: 'dialog-cancel-button',
          },
          {
            disabled: disableConfirmationSendButton,
            label: t('vpc:requestConsentConfirmationDialog:sendButton'),
            onClick: onClickConfirmationDialogSend,
            testId: 'dialog-send-button',
          },
        ]}
        body={
          <Trans
            i18nKey={'vpc:requestConsentConfirmationDialog:body'}
            values={{
              email,
              username: getDisplayName(authState) || t('vpc:platformDisplayName.unknownGamertag'),
              platform: t(getPlatformTranslationKey(platform)),
            }}
            components={{
              CustomLink: (
                <Link
                  onClick={handleLogout}
                  to="/"
                  aria-label="change-platform-link"
                  data-testid="change-platform-link"
                  style={{
                    color: theme.palette.primary.dark,
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                  }}
                />
              ),
            }}
          />
        }
      />
    </Box>
  )
}

export default ChildLandingPage
